const MODULE_NAME = 'plugin__axiosRequestCount'
const ACTION_DECREASE = `${MODULE_NAME}/decrease`
const ACTION_INCREASE = `${MODULE_NAME}/increase`

const vuexModule = {
  namespaced: true,

  state: () => {
    return {
      // This can be accessed from components as:
      // this.$store.state.plugin__axiosRequestCount.requestCount
      requestCount: 0,
    }
  },

  getters: {
    /**
     * @return {Number} the current request count
     */
    requestCount: state => state.requestCount,
  },

  actions: {
    increase({ state, commit }) {
      commit('setRequestCount', state.requestCount + 1)
    },

    decrease({ state, commit }) {
      commit('setRequestCount', state.requestCount - 1)
    },
  },

  mutations: {
    setRequestCount(state, requestCount) {
      state.requestCount = Math.max(parseInt(requestCount, 10) || 0, 0)
    },
  },
}

/**
 * @var {Map<require('axios').AxiosInstance, InterceptorHandlerMap>} clients
 */
const clients = new Map()

/**
 * Factory to create a vuex plugin responsible to track requests from a given axios
 * instance.
 *
 * @param {import('axios').AxiosInstance} axiosInstance An axios instance to hook interceptors to count requests
 * @return {import('vuex').Plugin} the Vuex plugin responsible for tracking axios client requests/response count
 */
export default function axiosRequestCountPlugin(axiosInstance) {
  /**
   * @param {require('vuex').Store} store
   */
  return (store) => {
    if (store.hasModule(MODULE_NAME) === false) {
      store.registerModule(MODULE_NAME, vuexModule)
    }

    if (clients.has(axiosInstance) === false) {
      clients.set(axiosInstance, { requestHandlerId: null, responseHandlerId: null })
    }

    /** @type {InterceptorHandlerMap} */
    const clientHandlers = clients.get(axiosInstance)

    if (clientHandlers.requestHandlerId === null) {
      clientHandlers.requestHandlerId = axiosInstance.interceptors.request.use(
        (config) => {
          store.dispatch(ACTION_INCREASE)
          return config
        },

        (error) => {
          store.dispatch(ACTION_DECREASE)
          return Promise.reject(error)
        },
      )
    }

    if (clientHandlers.responseHandlerId === null) {
      clientHandlers.responseHandlerId = axiosInstance.interceptors.response.use(
        (response) => {
          store.dispatch(ACTION_DECREASE)
          return response
        },

        (error) => {
          store.dispatch(ACTION_DECREASE)
          return Promise.reject(error)
        },
      )
    }
  }
}

/**
 * Allows a consumer of the plugin to unregister its related interceptors to free up resources
 *
 * @param {import('axios').AxiosInstance} axiosInstance
 */
export function unregisterClient(axiosInstance) {
  if (clients.has(axiosInstance) === false) {
    return false
  }

  /** @var {InterceptorHandlerMap} handlers */
  const handlers = clients.get(axiosInstance)
  axiosInstance.interceptors.request.eject(handlers.requestHandlerId)
  axiosInstance.interceptors.response.eject(handlers.requestHandlerId)
  return true
}

/**
 * @typedef {{requestHandlerId?: number, responseHandlerId?: number}} InterceptorHandlerMap
 */
